// InfoBar.js
//
// Top-center info strip: Scale · GSD · Datum · Zone.
//
// Why this lives on its own:
//   - The same four values are already rendered inside the top-right overlay
//     in ImageEditor (around the SCALE/GSD/DATUM/ZONE block). That overlay
//     is being retired in favor of the new noun-first UI; pulling the data
//     out into a dedicated component makes it trivial to position the info
//     strip independently (top-center, just below the primary toolbar) and
//     lets us remove the old block once the new surface covers everything.
//   - Info is passive — no interactivity, so it can safely overlap clickable
//     layers with `pointer-events: none` on the wrapper. This keeps the bar
//     from stealing drags from the canvas underneath.
//
// Each chip is label + value. Missing / loading values render as a muted
// placeholder so the layout doesn't jump when geo metadata finally resolves.

import React from 'react';

const chipStyle = {
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: '6px',
  padding: '4px 10px',
  borderRadius: '6px',
  backgroundColor: 'rgba(255,255,255,0.92)',
  border: '1px solid #cfd7d0',
  fontFamily: 'Arial, sans-serif',
  fontSize: '0.75rem',
  color: '#223',
  whiteSpace: 'nowrap',
};

const labelStyle = {
  fontWeight: 600,
  letterSpacing: '0.04em',
  color: '#556',
  fontSize: '0.65rem',
  textTransform: 'uppercase',
};

const valueStyle = {
  fontWeight: 700,
  color: '#111',
};

const placeholderStyle = {
  ...valueStyle,
  color: '#999',
  fontWeight: 500,
};

// `value` can be a string/number/JSX. When it's null/undefined/empty,
// render the placeholder instead.
const Chip = ({ label, value, placeholder = '—' }) => {
  const hasValue = value !== null && value !== undefined && value !== '';
  return (
    <span style={chipStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={hasValue ? valueStyle : placeholderStyle}>
        {hasValue ? value : placeholder}
      </span>
    </span>
  );
};

// Inline key cap, matching the shortcut-help overlay's <kbd> styling.
const Key = ({ children }) => (
  <kbd style={{
    display: 'inline-block',
    padding: '0px 5px',
    border: '1px solid #cfd7d0',
    borderBottomWidth: 2,
    borderRadius: 3,
    backgroundColor: '#f7f9f7',
    fontFamily: 'monospace',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#223',
  }}>{children}</kbd>
);

const InfoBar = ({ gsd, datum, zone, selectMode = false }) => {
  // GSD needs formatting; datum/zone come through as-is. Scale (zoom
  // level) was removed — it changed on every scroll and the number
  // didn't help users make decisions. If we ever want it back, add
  // a chip here and thread `scale` back into the props.
  const gsdFormatted = (typeof gsd === 'number' && isFinite(gsd))
    ? `${gsd.toFixed(5)} cm/px`
    : null;

  return (
    <div
      role="status"
      aria-label="Image info"
      style={{
        // Top-center per v2 redesign. The primary toolbar has moved to the
        // bottom, so we can sit right at the top of the viewport.
        position: 'fixed',
        top: '12px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'row',
        gap: '6px',
        zIndex: 1100,
        pointerEvents: 'none', // passive readout — don't block canvas drags
      }}
    >
      <Chip label="GSD" value={gsdFormatted} placeholder="calculating…" />
      <Chip label="Datum" value={datum} placeholder="waiting…" />
      <Chip label="Zone" value={zone} placeholder="waiting…" />
      {/* Select mode disables drag-to-pan (the drag is a marquee), so the
          only way to pan is holding Space. That was invisible — users hit a
          dead canvas and assumed panning was broken. Surface it exactly when
          it applies rather than burying it in the ? overlay alone. */}
      {selectMode && (
        <span style={chipStyle}>
          <Key>Space</Key>
          <span style={{ ...valueStyle, fontWeight: 600 }}>hold to pan</span>
          <span style={{ color: '#889' }}>·</span>
          <Key>⌘⇧M</Key>
          <span style={{ ...valueStyle, fontWeight: 600 }}>move by amount</span>
        </span>
      )}
    </div>
  );
};

export default InfoBar;
